import { FormEvent, useMemo, useState } from 'react'
import { Plus, Users, Pencil, Check, X, Upload, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useDemoContext } from '../DemoContext'
import type { ClassSummary, StudentSummary } from '../../api/types'

export default function DemoClasses() {
  const { classes, addClass, studentsByClass, addStudent, activeRole } = useDemoContext()
  const { t } = useTranslation('pages')

  const [selectedClassId, setSelectedClassId] = useState<number | null>(
    classes.length > 0 ? classes[0].id : null,
  )
  const [newClassName, setNewClassName] = useState('')
  const [newStudentName, setNewStudentName] = useState('')
  const [newStudentNo, setNewStudentNo] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Bulk import modal state
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [bulkListText, setBulkListText] = useState('')

  // Inline edit state
  const [editingStudentId, setEditingStudentId] = useState<number | null>(null)
  const [editingStudentName, setEditingStudentName] = useState('')
  const [editingStudentNo, setEditingStudentNo] = useState('')

  const canManage = activeRole === 'ORG_MANAGER' || activeRole === 'ADMIN'

  const selectedClass = useMemo(
    () => classes.find((item) => item.id === selectedClassId) || null,
    [classes, selectedClassId],
  )

  const students = selectedClassId ? studentsByClass[selectedClassId] || [] : []

  const onCreateClass = (event: FormEvent) => {
    event.preventDefault()
    const trimmed = newClassName.trim()
    if (!trimmed) {
      setError(t('demo.classes.nameRequired'))
      return
    }
    setError(null)
    const newId = Date.now()
    const created: ClassSummary = {
      id: newId,
      name: trimmed,
      subscriber_id: 1,
      lead_teacher_user_id: null,
      created_at: new Date().toISOString(),
    }
    addClass(created)
    setSelectedClassId(newId)
    setNewClassName('')
  }

  const onAddStudent = (event: FormEvent) => {
    event.preventDefault()
    if (!selectedClassId) {
      setError(t('demo.classes.selectClassFirst'))
      return
    }
    const fullName = newStudentName.trim()
    const studentNo = newStudentNo.trim()
    if (!fullName || !studentNo) {
      setError(t('demo.classes.nameNumberRequired'))
      return
    }
    setError(null)
    const newId = Date.now()
    const student: StudentSummary = {
      id: newId,
      full_name: fullName,
      student_no: studentNo,
      subscriber_id: 1,
    }
    addStudent(selectedClassId, student)
    setNewStudentName('')
    setNewStudentNo('')
  }

  const onBulkImport = () => {
    if (!selectedClassId) {
      setError(t('demo.classes.selectClassFirst'))
      return
    }
    const lines = bulkListText
      .trim()
      .split('\n')
      .filter((line) => line.trim())
    if (lines.length === 0) {
      setError(t('demo.classes.listEmpty'))
      return
    }
    setError(null)
    let successCount = 0
    let failCount = 0
    for (const line of lines) {
      const parts = line.trim().split(/\s+/)
      if (parts.length < 2) {
        failCount++
        continue
      }
      const studentNo = parts[parts.length - 1]
      const fullName = parts.slice(0, -1).join(' ')
      if (!fullName || !studentNo) {
        failCount++
        continue
      }
      const newId = Date.now() + successCount
      addStudent(selectedClassId, {
        id: newId,
        full_name: fullName,
        student_no: studentNo,
        subscriber_id: 1,
      })
      successCount++
    }
    setShowBulkModal(false)
    setBulkListText('')
    if (failCount > 0) {
      setError(t('demo.classes.bulkAddResult', { added: successCount, failed: failCount }))
    }
  }

  const startStudentEdit = (student: StudentSummary) => {
    setEditingStudentId(student.id)
    setEditingStudentName(student.full_name)
    setEditingStudentNo(student.student_no)
    setError(null)
  }

  const cancelStudentEdit = () => {
    setEditingStudentId(null)
    setEditingStudentName('')
    setEditingStudentNo('')
  }

  const saveStudentEdit = () => {
    // In demo, editing is a no-op (just close the edit)
    cancelStudentEdit()
  }

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <h2 className="page-header-title">{t('demo.classes.title')}</h2>
        <p className="page-header-sub">{t('demo.classes.subtitle')}</p>
      </div>

      {error && (
        <div
          className="card card-padding mb-20"
          style={{ borderLeft: '3px solid var(--color-danger)' }}
        >
          <p className="text-sm" style={{ color: 'var(--color-danger)' }}>
            {error}
          </p>
        </div>
      )}

      <div className="two-col-layout">
        {/* Left column — class list + create */}
        <div>
          {canManage && (
            <div className="card card-padding mb-20">
              <h3 className="card-title mb-16">{t('demo.classes.createCard')}</h3>
              <form className="flex gap-12" onSubmit={onCreateClass}>
                <div className="form-group" style={{ flex: 1 }}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder={t('demo.classes.namePlaceholder')}
                    value={newClassName}
                    onChange={(e) => setNewClassName(e.target.value)}
                  />
                </div>
                <button className="btn btn-primary" type="submit">
                  <Plus size={16} />
                  {t('common.create')}
                </button>
              </form>
            </div>
          )}

          <div className="card">
            {classes.length === 0 ? (
              <div className="card-padding">
                <p className="text-sm text-muted">
                  {t('demo.classes.classesEmpty')}
                </p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('demo.classes.classCol')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {classes.map((c) => {
                      const isSelected = selectedClassId === c.id
                      return (
                        <tr
                          key={c.id}
                          onClick={() => setSelectedClassId(c.id)}
                          style={{ cursor: 'pointer' }}
                          className={isSelected ? 'table-row-selected' : ''}
                        >
                          <td className="font-semibold">{c.name}</td>
                          <td></td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right column — students of selected class */}
        <div>
          <div className="card card-padding">
            <div className="flex items-center justify-between mb-16">
              <div>
                <h3 className="card-title">
                  {selectedClass ? selectedClass.name : t('demo.classes.studentsHeading')}
                </h3>
                <p className="text-sm text-muted mt-4">
                  {selectedClass
                    ? t('demo.classes.nStudents', { count: students.length })
                    : t('demo.classes.selectClassHint')}
                </p>
              </div>
              {selectedClass && (
                <span className="badge badge-copper">
                  <Users size={12} />
                  {students.length}
                </span>
              )}
            </div>

            {selectedClass && canManage && (
              <form className="mb-20" onSubmit={onAddStudent}>
                <div className="flex gap-12">
                  <div className="form-group" style={{ flex: 1 }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder={t('demo.classes.studentNamePlaceholder')}
                      value={newStudentName}
                      onChange={(e) => setNewStudentName(e.target.value)}
                    />
                  </div>
                  <div className="form-group" style={{ width: 140 }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder={t('demo.classes.studentNumberPlaceholder')}
                      value={newStudentNo}
                      onChange={(e) => setNewStudentNo(e.target.value)}
                    />
                  </div>
                  <button className="btn btn-primary" type="submit">
                    <Plus size={16} />
                    {t('common.add')}
                  </button>
                  <button
                    className="btn btn-outline"
                    type="button"
                    onClick={() => setShowBulkModal(true)}
                  >
                    <Upload size={16} />
                    {t('demo.classes.bulkUpload')}
                  </button>
                </div>
              </form>
            )}

            {selectedClass && (
              <div
                className="table-wrapper"
                style={{ maxHeight: 460, overflowY: 'auto' }}
              >
                {students.length === 0 ? (
                  <p className="text-sm text-muted" style={{ padding: 12 }}>
                    {t('demo.classes.studentsEmpty')}
                  </p>
                ) : (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{t('demo.classes.colHash')}</th>
                        <th>{t('demo.classes.colFullName')}</th>
                        <th>{t('demo.classes.colStudentNo')}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((s, i) => {
                        const isEditing = editingStudentId === s.id
                        return (
                          <tr key={s.id}>
                            <td className="text-muted text-xs">{i + 1}</td>
                            <td>
                              {isEditing ? (
                                <input
                                  className="form-input"
                                  value={editingStudentName}
                                  onChange={(e) =>
                                    setEditingStudentName(e.target.value)
                                  }
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '0.85rem',
                                  }}
                                />
                              ) : (
                                <span className="font-semibold">{s.full_name}</span>
                              )}
                            </td>
                            <td>
                              {isEditing ? (
                                <input
                                  className="form-input"
                                  value={editingStudentNo}
                                  onChange={(e) =>
                                    setEditingStudentNo(e.target.value)
                                  }
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '0.85rem',
                                  }}
                                />
                              ) : (
                                <span className="text-muted">{s.student_no}</span>
                              )}
                            </td>
                            <td>
                              {canManage && (
                                <>
                                  {!isEditing ? (
                                    <button
                                      className="btn btn-icon btn-ghost"
                                      onClick={() => startStudentEdit(s)}
                                      title={t('common.edit')}
                                    >
                                      <Pencil
                                        size={14}
                                        style={{ color: 'var(--text-muted)' }}
                                      />
                                    </button>
                                  ) : (
                                    <div className="flex gap-4">
                                      <button
                                        className="btn btn-icon btn-ghost"
                                        onClick={saveStudentEdit}
                                        title={t('common.save')}
                                      >
                                        <Check
                                          size={14}
                                          style={{ color: 'var(--color-success)' }}
                                        />
                                      </button>
                                      <button
                                        className="btn btn-icon btn-ghost"
                                        onClick={cancelStudentEdit}
                                        title={t('common.cancel')}
                                      >
                                        <X
                                          size={14}
                                          style={{ color: 'var(--color-danger)' }}
                                        />
                                      </button>
                                    </div>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Import Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('demo.classes.bulkModalTitle')}</h3>
              <button
                className="btn btn-icon btn-ghost"
                onClick={() => setShowBulkModal(false)}
              >
                <XCircle size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p className="text-sm text-muted mb-16">
                {t('demo.classes.bulkModalDesc')} <strong>{t('demo.classes.bulkColName')} {t('demo.classes.bulkColNumber')}</strong>
              </p>
              <div className="bulk-import-header">
                <span>{t('demo.classes.bulkColName')}</span>
                <span>{t('demo.classes.bulkColNumber')}</span>
              </div>
              <textarea
                className="form-input"
                style={{
                  height: 240,
                  fontFamily: 'monospace',
                  resize: 'vertical',
                }}
                placeholder={'Ahmet Yilmaz 123\nMehmet Demir 456\nAyse Kaya 789'}
                value={bulkListText}
                onChange={(e) => setBulkListText(e.target.value)}
              />
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-ghost"
                onClick={() => setShowBulkModal(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={onBulkImport}
                disabled={!bulkListText.trim()}
              >
                {t('common.add')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
